import { TCreateUsageRequest, TUpdateUsageRequest, TUsageId, Usage, UsageService } from '@talon/web/lib/api';
import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore'; // for some dumb reason, these have to be imported individually
import { RootStore } from './rootStore'; // for some dumb reason, these have to be imported individually

export class UsageStore extends BaseStore {
  currentUsage: Usage | undefined = undefined;
  usageList: Usage[] = [];
  usageTypeInfo: Record<string, unknown> = {};

  isSaving = false;

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      currentUsage: observable,
      usageList: observable,
      usageTypeInfo: observable,
      isSaving: observable,
    });
  }

  setIsSaving(isSaving: boolean) {
    runInAction(() => (this.isSaving = isSaving));
  }

  setCurrentUsage(usage?: Usage) {
    runInAction(() => (this.currentUsage = usage));
  }

  setUsageList(usageList: Usage[]) {
    runInAction(() => (this.usageList = usageList));
  }

  listUsages = async (relations = 'usage_estimate') => {
    try {
      const { usages } = await UsageService.listUsages(undefined, undefined, undefined, relations);
      this.setUsageList(usages);
      return usages;
    } catch (e) {
      this.handleError(e);
    }
  };

  getUsage = async (usageId: TUsageId, relations = 'usage_estimate') => {
    try {
      const { usage } = await UsageService.getUsage(usageId, relations);
      return usage;
    } catch (e) {
      this.handleError(e);
    }
  };

  createUsage = async (usageData: TCreateUsageRequest) => {
    try {
      this.setIsSaving(true);
      const { usage } = await UsageService.createUsage(usageData);
      if (usage) {
        runInAction(() => {
          this.usageList.push(usage);
        });
        return usage;
      }
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  updateUsage = async (usageId: TUsageId, updateRequest: TUpdateUsageRequest, isTempEstimate?: boolean) => {
    if (!usageId) return;
    try {
      this.setIsSaving(true);

      await UsageService.updateUsage(usageId, updateRequest);

      const fullUpdatedUsage = await this.getUsage(usageId);

      if (fullUpdatedUsage) {
        runInAction(() => {
          const i = this.usageList?.findIndex(({ id }) => id === usageId) ?? -1;
          if (i > -1) {
            this.usageList.splice(i, 1, fullUpdatedUsage);
          } else {
            this.usageList.push(fullUpdatedUsage);
          }
        });

        // update displayed estimate values
        const est = await this.rootStore.estimateStore.getEstimate(
          isTempEstimate
            ? this.rootStore.estimateStore.tempEstimate?.id
            : this.rootStore.estimateStore.currentEstimate?.id,
          'usage_estimates,session_estimates'
        );
        est && isTempEstimate
          ? this.rootStore.estimateStore.setTempEstimate(est)
          : this.rootStore.estimateStore.setCurrentEstimate(est);

        return fullUpdatedUsage;
      }
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteUsage = async (usageId: TUsageId) => {
    try {
      this.setIsSaving(true);
      await UsageService.deleteUsage(usageId);
      if (usageId === this.currentUsage?.id) this.setCurrentUsage(undefined);
      runInAction(() => {
        this.usageList = this.usageList?.filter((p) => p.id !== usageId);
      });
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  getUsageTypeData = async () => {
    try {
      if (Object.keys(this.usageTypeInfo).length > 0) {
        return this.usageTypeInfo;
      } else {
        runInAction(async () => {
          const usage_types = await UsageService.getUsageTypeData();
          this.usageTypeInfo = usage_types;
          return usage_types;
        });
      }
    } catch (e) {
      this.handleError(e);
    }
  };
}
